import React from "react";
import {
  BooleanField,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  Pagination,
  TextField,
  TopToolbar,
} from "react-admin";
import keyBy from "lodash/keyBy";
import { ButtonGroup, Dialog, DialogContent, IconButton, Tooltip, Zoom } from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { DetailButton } from "../../../Components/DetailButton";
import DownloadIcon from "@material-ui/icons/GetApp";
import { ExcelExport } from "../../../Helper/ExportExcel";
import {
  ServiceableServiceLevelDetailHeaders,
  ServiceableServiceLevelDetailHeadersTtUser,
  ServiceableServiceLevelDetailName,
} from "./Excel";
import { CustomDateField } from "../../../Components/CustomDateField";
import { getTkCargoTrackingUrl } from "../../../Helper/TkCargoHelper";
import { useAuth } from "@airborne/react-admin";
import { getDateDifferenceInDays } from "../../..//Helper/DateHelper";

const ServiceableServiceLevelDetailComponent = props => {
  const { user } = useAuth();
  const [page, setPage] = React.useState(1);
  const [open, setOpen] = React.useState(false);
  const perPage = 5;

  const GetTempData = () => {
    return props?.data.slice((page - 1) * perPage, perPage * page);
  };

  React.useEffect(() => {
    setOpen(props.open);
  }, [props.open]);

  const handleClose = () => {
    setOpen(false);
    props?.handleClose?.();
  };

  const ExportToExcel = () => {
    const headers = user?.isCustomer
      ? ServiceableServiceLevelDetailHeaders
      : ServiceableServiceLevelDetailHeadersTtUser;

    ExcelExport(
      headers,
      props?.data.map(v => [
        v.contractName,
        v.refNo,
        v.category,
        v.requestedPn,
        v.serviceableAwb,
        v.serviceableForwardDate,
        v.unserviceableForwardDate,
        v.requestedDeliveryDate,
        v.customerPo,
        v.isDelayed,
        ...(!user?.isCustomer
          ? [
              v.cargoAcceptedDate,
              v.cargoAcceptedLocation,
              v.cargoDepartureDate,
              v.cargoDepartureLocation,
              v.cargoDepartureReference,
              v.cargoArrivalDate,
              v.cargoArrivalLocation,
              v.cargoArrivalReference,
              getDateDifferenceInDays(v.cargoAcceptedDate, v.cargoArrivalDate),
              getDateDifferenceInDays(v.serviceableForwardDate, v.cargoArrivalDate),
              getDateDifferenceInDays(v.serviceableForwardDate, v.cargoAcceptedDate),
            ]
          : []),
      ]),
      ServiceableServiceLevelDetailName
    );
  };

  const ExportExcelButton = props => {
    return (
      <IconButton style={{ width: 40, color: "#C90C0F" }} onClick={ExportToExcel} disabled={props?.data === 0}>
        <DownloadIcon />
      </IconButton>
    );
  };

  const ListActions = props => (
    <TopToolbar>
      <ExportExcelButton />
    </TopToolbar>
  );

  return (
    <React.Fragment>
      <Dialog
        fullWidth={true}
        maxWidth={"xl"}
        open={open}
        onClose={handleClose}
        aria-labelledby="max-width-dialog-title"
      >
        <DialogContent>
          <ListContextProvider
            value={{
              data: keyBy(GetTempData(), "id"),
              ids: GetTempData().map(({ id }) => id),
              currentSort: { field: "id", order: "DESC" },
            }}
          >
            <ListToolbar actions={<ListActions />} />
            <Datagrid>
              <FunctionField
                style={{ fontSize: 12 }}
                render={(record: any) => {
                  return (
                    <ButtonGroup variant="text" size="small">
                      <DetailButton record={record} resource={"pbhrequestorders"} />

                      {!user?.isCustomer && record.serviceableAwb && (
                        <IconButton
                          style={{ width: 40 }}
                          onClick={() => window.open(getTkCargoTrackingUrl(record.serviceableAwb), "_blank")}
                        >
                          <Tooltip TransitionComponent={Zoom} title="Open TK Cargo Tracking Page">
                            <OpenInNewIcon />
                          </Tooltip>
                        </IconButton>
                      )}
                    </ButtonGroup>
                  );
                }}
              />
              <TextField source="contractName" label="Contract" />
              <TextField source="refNo" label="Ref No" />
              <TextField source="category" label="Category" />
              <TextField source="requestedPn" label="Requested Pn" />
              <TextField source="serviceableAwb" label="Serviceable Awb" />
              <CustomDateField showTime={true} source="serviceableForwardDate" label="Serviceable Forward Date" />
              <CustomDateField showTime={true} source="unserviceableForwardDate" label="Core Unit Forward Date" />
              <CustomDateField showTime={true} source="requestedDeliveryDate" label="Requested Delivery Date" />
              <TextField source="customerPo" label="Customer Po" />
              <FunctionField
                style={{ fontSize: 12 }}
                label="Delayed"
                render={(record: any) => (
                  <BooleanField
                    source="isDelayed"
                    label="Delayed"
                    style={{ color: record.isDelayed ? "#FF0000" : "#45B4C8" }}
                  />
                )}
              />

              {/* ── TK Cargo milestone kolonları ─────────────────────────────────── */}
              {!user?.isCustomer && (
                <CustomDateField showTime={true} source="cargoAcceptedDate" label="Cargo Accepted (RCS)" />
              )}
              {!user?.isCustomer && <TextField source="cargoAcceptedLocation" label="Accepted Location" />}
              {!user?.isCustomer && (
                <CustomDateField showTime={true} source="cargoDepartureDate" label="Cargo Departure (DEP)" />
              )}
              {!user?.isCustomer && <TextField source="cargoDepartureLocation" label="Departure Location" />}
              {!user?.isCustomer && <TextField source="cargoDepartureReference" label="Departure Flight" />}
              {!user?.isCustomer && (
                <CustomDateField showTime={true} source="cargoArrivalDate" label="Cargo Arrival (RCF)" />
              )}
              {!user?.isCustomer && <TextField source="cargoArrivalLocation" label="Arrival Location" />}
              {!user?.isCustomer && <TextField source="cargoArrivalReference" label="Arrival Flight" />}
              {!user?.isCustomer && (
                <FunctionField
                  label="Transit Days (RCS→RCF)"
                  render={(record: any) => getDateDifferenceInDays(record?.cargoAcceptedDate, record?.cargoArrivalDate)}
                />
              )}
              {!user?.isCustomer && (
                <FunctionField
                  label="Fwd→Arrival Days (Forward→RCF)"
                  render={(record: any) =>
                    getDateDifferenceInDays(record?.serviceableForwardDate, record?.cargoArrivalDate)
                  }
                />
              )}
              {!user?.isCustomer && (
                <FunctionField
                  label="Waiting Days (Forward→RCS)"
                  render={(record: any) =>
                    getDateDifferenceInDays(record?.serviceableForwardDate, record?.cargoAcceptedDate)
                  }
                />
              )}
            </Datagrid>
            <Pagination
              //@ts-ignore
              page={page}
              perPage={perPage}
              setPage={setPage}
              total={props?.data.length}
            />
          </ListContextProvider>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default ServiceableServiceLevelDetailComponent;
